// V3 Inventory Console — items + movements + low-stock su /api/v2
// (cap_inventory). Ledger append-only + stock GREATEST(0) — il movimento
// è l'unica scrittura sulla giacenza.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });
const badge = (s: string): React.CSSProperties => ({
  padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
  background: s === 'in' ? '#dcfce7' : '#fee2e2', color: s === 'in' ? '#166534' : '#991b1b',
});

export function V3InventoryPage() {
  const [items, setItems] = useState<Row[]>([]);
  const [movements, setMovements] = useState<Row[]>([]);
  const [low, setLow] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', sku: '', category: '', unit: 'pz', price: '', cost: '', stock_qty: '0', min_stock: '0' });
  const [mov, setMov] = useState({ item_id: '', movement_type: 'in', quantity: '1', reason: '' });

  const load = useCallback(async () => {
    try {
      const [i, m, l] = await Promise.all([
        apiFetch('/api/v2/inventory-items'), apiFetch('/api/v2/inventory-movements'),
        apiFetch('/api/v2/inventory-low-stock'),
      ]);
      setItems(await i.json()); setMovements(await m.json()); setLow(await l.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const createItem = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/inventory-items', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name, sku: form.sku || null, category: form.category || null,
          unit: form.unit, price: Number(form.price) || 0, cost: Number(form.cost) || 0,
          stock_qty: Number(form.stock_qty) || 0, min_stock: Number(form.min_stock) || 0,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setForm({ name: '', sku: '', category: '', unit: 'pz', price: '', cost: '', stock_qty: '0', min_stock: '0' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore articolo');
    } finally {
      setSaving(false);
    }
  };

  const createMovement = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/inventory-movements', {
        method: 'POST',
        body: JSON.stringify({
          item_id: Number(mov.item_id), movement_type: mov.movement_type,
          quantity: Number(mov.quantity), reason: mov.reason || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setMov({ item_id: '', movement_type: 'in', quantity: '1', reason: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore movimento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Nuovo articolo</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <input style={input} placeholder="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input style={input} placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            <input style={input} placeholder="Categoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={input} placeholder="Prezzo" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              <input style={input} placeholder="Costo" type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={input} placeholder="Giacenza" type="number" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} />
              <input style={input} placeholder="Scorta min" type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
            </div>
            <button style={btn()} disabled={saving || !form.name} onClick={() => void createItem()}>
              {saving ? 'Salvataggio…' : 'Crea articolo'}
            </button>
          </div>
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Movimento stock</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={mov.item_id} onChange={(e) => setMov({ ...mov, item_id: e.target.value })}>
              <option value="">Articolo…</option>
              {items.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.stock_qty})</option>)}
            </select>
            <select style={input} value={mov.movement_type} onChange={(e) => setMov({ ...mov, movement_type: e.target.value })}>
              <option value="in">Carico (+)</option>
              <option value="out">Scarico (−)</option>
              <option value="sale">Vendita (−)</option>
              <option value="waste">Spreco (−)</option>
              <option value="adjustment">Rettifica (−)</option>
            </select>
            <input style={input} placeholder="Quantità" type="number" min={1} value={mov.quantity}
              onChange={(e) => setMov({ ...mov, quantity: e.target.value })} />
            <input style={input} placeholder="Motivo" value={mov.reason} onChange={(e) => setMov({ ...mov, reason: e.target.value })} />
            <button style={btn(mov.movement_type === 'in' ? '#166534' : '#991b1b')} disabled={saving || !mov.item_id} onClick={() => void createMovement()}>
              {saving ? '…' : 'Registra movimento'}
            </button>
          </div>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2 · {items.length} articoli · {movements.length} movimenti · {low.length} sotto scorta
        </p>
      </div>

      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        {low.length > 0 && (
          <div style={{ ...card, borderColor: '#f59e0b', background: '#fffbeb' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>⚠ Sotto scorta ({low.length})</h4>
            {low.map((l) => (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #fde68a', fontSize: 13 }}>
                <span><strong>{l.name}</strong>{l.brand ? ` · ${l.brand}` : ''}</span>
                <span style={{ color: '#991b1b' }}>{l.stock_qty}/{l.min_stock} (mancano {l.shortfall})</span>
              </div>
            ))}
          </div>
        )}

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Articoli ({items.length})</h4>
          {items.length === 0 && <p className="sk-muted">Nessun articolo.</p>}
          {items.map((i) => (
            <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span>
                <strong>{i.name}</strong>{i.sku ? ` · ${i.sku}` : ''}{i.category ? ` · ${i.category}` : ''}
              </span>
              <span style={{ color: Number(i.stock_qty) < Number(i.min_stock) ? '#991b1b' : '#166534' }}>
                {i.stock_qty} {i.unit || 'pz'}{Number(i.price) > 0 ? ` · €${i.price}` : ''}
              </span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Movimenti recenti (ledger)</h4>
          {movements.length === 0 && <p className="sk-muted">Nessun movimento.</p>}
          {movements.slice(0, 15).map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span>{m.item_name || `art. ${m.item_id}`}{m.reason ? ` · ${m.reason}` : ''}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={badge(m.movement_type)}>{m.movement_type === 'in' ? '+' : '−'}{m.quantity}</span>
                <span style={{ color: '#6b7280' }}>{String(m.created_at).slice(0, 16).replace('T', ' ')}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
